import './PayInvoiceSheet.sass';

import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import WebApp from '@twa-dev/sdk';

import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { Button, LottieIcon } from '$uikit';
import { mainActions } from '$store/main';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';
import { getPayErrorMessage } from './getMethodVisual';

interface PayInvoiceSheetProps {
  request: Promise<{ url?: string }>;
  onSuccess?: () => void;
}

export const PayInvoiceSheet: FC<PayInvoiceSheetProps> = ({ request, onSuccess }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const timer = setTimeout(() => {
      cancelled = true;
      dispatch(mainActions.setPopup(null));
      dispatch(toastActions.setToastFail());
    }, 30_000);

    request
      .then((resp) => {
        if (cancelled) return;
        clearTimeout(timer);
        if (!resp?.url) {
          dispatch(mainActions.setPopup(null));
          dispatch(toastActions.setToastFail());
          return;
        }
        setUrl(resp.url);
        onSuccess?.();
        try {
          if (window.isWebApp) {
            WebApp.openLink(resp.url);
          } else {
            location.href = resp.url;
          }
        } catch {}
      })
      .catch((e) => {
        if (cancelled) return;
        clearTimeout(timer);
        dispatch(mainActions.setPopup(null));
        dispatch(toastActions.setToastFail(getPayErrorMessage(e, t)));
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const handleOpen = useCallback(() => {
    if (!url) return;
    if (window.isWebApp) {
      WebApp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  }, [url]);

  const handleClose = useCallback(() => {
    dispatch(mainActions.setPopup(null));
  }, [dispatch]);

  return (
    <BottomSheet
      title={t('top_up_balance_title')}
      className="PayInvoiceSheet"
      dismissible={!!url}
      closeButtonShown={!!url}
    >
      <div className="PayInvoiceSheet__content">
        <div className="PayInvoiceSheet__icon">
          <LottieIcon size={110} name="money2" loop />
        </div>
        {url ? (
          <>
            <div className="PayInvoiceSheet__title">
              {t('top_up_redirect_title', { defaultValue: 'Invoice is ready' })}
            </div>
            <div className="PayInvoiceSheet__caption">
              {t('top_up_redirect_caption', {
                defaultValue: 'Complete your top-up on the secure payment page.',
              })}
            </div>
            <div className="PayInvoiceSheet__actions">
              <Button stretched onClick={handleOpen}>
                {t('top_up_go_to_payment_button', { defaultValue: 'Go to payment' })}
              </Button>
              <Button stretched mode="secondary" onClick={handleClose}>
                {t('tips_close', { defaultValue: 'Close' })}
              </Button>
            </div>
          </>
        ) : (
          <div className="PayInvoiceSheet__loading">
            <div className="PayInvoiceSheet__loading__text">{t('loading')}</div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
};
